import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import AuthShell from "../../components/AuthShell/AuthShell";
import TextField from "../../components/primitives/TextField";
import TextButton from "../../components/primitives/TextButton";
import FormError from "../../components/primitives/FormError";
import { ApiError } from "../../services/api";
import { useAuth } from "../../services/AuthContext";
import "./auth.css";

const looksLikeEmail = (s) => /^\S+@\S+\.\S+$/.test(String(s ?? "").trim());

function ForgotPasswordPage() {
    const navigate = useNavigate();
    const { forgotPassword } = useAuth();
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [serverErr, setServerErr] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        ref.current?.focus?.();
    }, []);

    const submit = async (e) => {
        e?.preventDefault?.();
        setServerErr(null);
        if (!looksLikeEmail(email)) {
            setServerErr("Enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            await forgotPassword(email.trim());
            navigate("/auth/check-email", {
                state: { email: email.trim(), intent: "reset" },
                replace: true,
            });
        } catch (err) {
            if (err instanceof ApiError && err.code === "too_many_requests") {
                setServerErr("Too many attempts. Try again in a minute.");
            } else {
                setServerErr(
                    err instanceof ApiError
                        ? err.message
                        : err?.message || "Could not send reset link."
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthShell
            eyebrow="Console / Forgot password"
            title="Reset your password."
            lede="Enter your email and we'll send a link to set a new password."
            footer={
                <>
                    <span>Remembered it?</span>
                    <Link to="/login" className="auth-shell-link auth-shell-link--accent">
                        Back to sign-in →
                    </Link>
                </>
            }
        >
            <form className="auth-form" onSubmit={submit} noValidate>
                <TextField
                    ref={ref}
                    type="email"
                    label="Email"
                    required
                    value={email}
                    onChange={setEmail}
                    placeholder="you@company.com"
                    autoComplete="email"
                />

                {serverErr && <FormError>{serverErr}</FormError>}

                <TextButton
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={submitting}
                    iconRight={ArrowRight}
                >
                    Send reset link
                </TextButton>
            </form>
        </AuthShell>
    );
}

export default ForgotPasswordPage;
